/*
 * AgentTransport — Abstrakte Schnittstelle fuer Agent-Transports.
 *
 * Der Transport ist der Koppelpunkt zwischen UI (CoreConsole, ReviewController)
 * und der Agent-Logik. Zwei konkrete Implementierungen:
 *
 *  - MockAgentTransport: deterministisches Scenario-Playback (lokal, Dev-only)
 *  - RealAgentTransport: SSE-Stream vom Server (spaeter in Agent-Phase 1)
 *
 * Beide teilen dieselbe Oberflaeche, damit ReviewController und die Console
 * beim Umschalten zwischen Mock und Real keinen Unterschied sehen.
 *
 * Handler-Shape (Superset der heutigen ChatService-Callbacks):
 *  - onSession({ sessionId })              Session-ID vom Server / Mock
 *  - onThinking({ text })                  Extended-Thinking-Delta
 *  - onThinkingDone()                      Thinking-Block abgeschlossen
 *  - onDelta({ text })                     Textdelta (assistant prose)
 *  - onToolCall({ toolCallId, tool, input })
 *  - onToolResult({ toolCallId, tool, summary })
 *
 * Format-Felder (seit Markdown-First-Migration vom 2026-05-02): Tools, die
 * Text-Felder anfassen, schicken neben `newText`/`note`/`initialText` immer
 * den vollen Patch — Inline-Formate, Section-Links und (fuer 'text'-Felder)
 * Block-Formate. Praefix folgt dem jeweiligen Text-Feld:
 *   text         → newText / newFormats / newLinks
 *                  + newHeadingLevel / newListType / newListLevel / newBlockquote
 *   note         → note / noteFormats / noteLinks (keine Block-Felder)
 *   initialText  → initialText / initialFormats / initialLinks
 *                  + initialHeadingLevel / initialListType / initialListLevel / initialBlockquote
 * Plain-Text-Faelle haben leere Format-Arrays und 0/null/false als Block-
 * Defaults. Konsumenten lesen mit `|| []` / `|| 0` / `|| null` defensiv.
 *
 *  - onProposeRewrite({ toolCallId, paragraphId, baseVersion, originalText, newText, newFormats, newLinks, newHeadingLevel, newListType, newListLevel, newBlockquote, reason })
 *  - onProposeInsert({ toolCallId, afterParagraphId, baseVersion, newText, newFormats, newLinks, newHeadingLevel, newListType, newListLevel, newBlockquote, reason })
 *  - onProposeDelete({ toolCallId, paragraphId, baseVersion, originalText, reason })
 *  - onProposeRewriteApply({ ...rewrite, area })
 *  - onProposeInsertApply({ ...insert, area })
 *  - onProposeDeleteApply({ toolCallId, paragraphId, baseVersion, originalText, area, reason })
 *  - onProposeSketch({ toolCallId, separatorId, area, afterParagraphId, items: [{outlineDepth, note, noteFormats, noteLinks}], reason })
 *  - onProposeWriteNote({ toolCallId, paragraphId, baseVersion, note?, noteFormats?, noteLinks?, outlineDepth? })
 *  - onProposeCreateSection({ toolCallId, area, afterSeparatorId, beforeParagraphId, title, depth, initialText?, initialFormats?, initialLinks?, initialHeadingLevel?, initialListType?, initialListLevel?, initialBlockquote? })
 *  - onProposeUpdateSection({ toolCallId, separatorId, baseVersion, title?, depth? })
 *  - onProposeDeleteSection({ toolCallId, separatorId })
 *  - onProposeUpdateUserMemory({ toolCallId, byteLength })
 *      User-Memory-Schreibvorgang (Phase 4 Sub-Phase 4.1). Server hat den
 *      Inhalt bereits in users.global_memory geschrieben — der Client ack-t
 *      nur fuer Console-Sichtbarkeit (modelMethod=null in der Auto-Apply-
 *      Tool-Map). KEIN Editor-Apply, KEINE Section-Beziehung.
 *  - onProposeUpdateContextInfo({ toolCallId, paragraphId, baseVersion, info, infoFormats, infoLinks })
 *      info: Markdown im Carbisdale-Dialekt (Inline + [[@id]]; keine
 *      Block-Formate auf info). infoFormats/infoLinks sind die vom Server
 *      geparsten Patch-Felder, gleicher Vertrag wie note in PROPOSE_WRITE_NOTE
 *      (Phase 3 Pattern: Tool-Input ist Markdown, SSE schickt Markdown PLUS
 *      geparsten Patch, damit der Client nicht doppelt parsen muss).
 *      Wird auch von set_context_link emittiert (Variante A) — kein eigenes
 *      Event-Type fuer den Section-Link-Pfad.
 *  - onProposeJumpToSection({ toolCallId, paragraphId, title, area })
 *      Lokale Viewport-Navigation (Phase 4 Sub-Phase 4.2). Client ruft
 *      EditorModel.jumpToSection (delegiert an EditorInput._navigateToLink),
 *      ack-t mit applied. KEINE DB-Mutation, KEIN Versions-Check.
 *  - onRateLimit({ retryAfter })
 *  - onDone({ usage })
 *  - onError(err)
 *  - onAborted({ reason })
 *  - onAwaitReviewStarted({ expecting, toolCallIds, mode, summary })
 *      Der Agent hat `await_review` aufgerufen — der Server-Loop laeuft im
 *      offenen SSE-Stream weiter und wartet auf die Verdicts. Der Client
 *      schickt sie via `transport.reportReviewDone(reviewResults)` zurueck
 *      statt sie fuer den naechsten /message-Body zu puffern.
 *  - onAwaitReviewDone({ status, verdicts, stillPending, mode })
 *      Bestaetigung, dass der Server-Waiter aufgeloest wurde
 *      (status='complete'|'timeout'). Der Loop iteriert direkt weiter,
 *      Folge-SSE-Events kommen ohne Pause.
 *
 * Alle Handler sind optional. Der Transport ruft nur auf, was vorhanden ist.
 *
 * Lifecycle:
 *   transport.sendMessage(message, context, handlers) -> Promise<void>
 *   transport.reportActionDone({ toolCallId, result, appliedVersion? }) -> Promise<void>
 *   transport.abort()
 *   transport.switchProject(projectId) -> Promise<void>
 *   transport.destroy()
 *
 * context shape:
 *   { projectId, cursorContext? }
 *
 * cursorContext: siehe docs/archive/ai-agent-specification.md §5 — wird in Phase A
 * noch nicht gefuellt; MockAgentTransport konsumiert ihn nur fuer Placeholder-
 * Resolution.
 */

/**
 * Abstrakte Basisklasse. Unterklassen implementieren die Streaming-Logik.
 */
export class AgentTransport {
    /**
     * Sendet eine User-Nachricht und streamt die Antwort via Handlers.
     * @param {string} message
     * @param {{ projectId?: string, cursorContext?: object }} context
     * @param {object} handlers - siehe Handler-Shape oben
     * @returns {Promise<void>}
     */
    async sendMessage(_message, _context, _handlers) {
        throw new Error('AgentTransport.sendMessage() must be implemented')
    }

    /**
     * Meldet das Ergebnis einer propose_*-Aktion zurueck (Apply / Reject /
     * Version-Mismatch). Reactive Scenarios koennen auf dieses Signal warten.
     * @param {{ toolCallId: string, result: 'applied'|'rejected'|'version_mismatch', appliedVersion?: number }} _info
     * @returns {Promise<void>}
     */
    async reportActionDone(_info) {
        // Optional — Default ist no-op
    }

    /**
     * Meldet ein Buendel von Review-Verdicts zurueck, waehrend der Server-Loop
     * in einem offenen `await_review` haengt (POST /api/agent/review-done).
     * Wird NICHT genutzt, wenn kein await aktiv ist — der ReviewController
     * puffert die Verdicts dann fuer den naechsten sendMessage-Aufruf.
     * @param {Array<{toolCallId:string, result:string, appliedVersion?:number}>} _reviewResults
     * @returns {Promise<{status:string, accepted?:number, complete?:boolean}|null>}
     */
    async reportReviewDone(_reviewResults) {
        // Optional — Default ist no-op
        return null
    }

    /**
     * @returns {boolean} ob der Server gerade auf Review-Verdicts wartet
     *                    (await_review aktiv). Der ReviewController nutzt das,
     *                    um zwischen reportReviewDone und Pufferung zu waehlen.
     */
    hasActiveAwaitReview() {
        return false
    }

    /** Bricht einen laufenden Request ab. */
    abort() {}

    /**
     * Projektwechsel (analog ChatService.switchProject).
     * @param {string} _projectId
     */
    async switchProject(_projectId) {}

    /** Raeumt Ressourcen auf. */
    destroy() {}
}
